import type { i18n as I18n } from 'i18next';
import { displaySpecifierValue, sanitizeOutcomeDisplay } from './outcome-labels';
import { feedTranslationKey, translateNamespacedLabel } from './resource-labels';

export const marketTranslationKey = feedTranslationKey;

/**
 * Resolve a market key (e.g. `1` / `br:1`) to its MarketTranslates label (e.g. `1x2`).
 */
export function translateMarketLabel(
  key: string,
  i18n: I18n,
  feedType?: number | null,
): string {
  return translateNamespacedLabel(key, i18n, 'market', feedType);
}

/** Remove `{total}`, `{hcp}`, and any other `{...}` tokens left in a market title. */
export function stripMarketPlaceholders(label: string): string {
  return sanitizeOutcomeDisplay(
    label
      .replace(/\{[^}]+\}/g, '')
      .replace(/\s{2,}/g, ' ')
      .replace(/\s+([)\].,!?:;])/g, '$1')
      .trim(),
  );
}

/**
 * Format a translated market title against its lines.
 * Alternative lines mean the specifier lives on each outcome row — strip `{total}` /
 * `{hcp}` / other placeholders from the card header. A single line fills them.
 * Feed template ids (`sr:exact_goals`) are never injected into the title.
 */
export function formatMarketTitle(
  label: string,
  options: { hasAlternativeLines: boolean; specifier?: string },
): string {
  if (options.hasAlternativeLines) {
    return stripMarketPlaceholders(label);
  }

  const displaySpecifier = displaySpecifierValue(options.specifier);
  if (displaySpecifier && /\{[^}]+\}/.test(label)) {
    return sanitizeOutcomeDisplay(
      label.replace(/\{[^}]+\}/g, displaySpecifier).replace(/\s{2,}/g, ' ').trim(),
    );
  }

  return stripMarketPlaceholders(label);
}
